import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2, TrendingUp, DollarSign, Target, AlertCircle, CheckCircle, Download, Calculator, Plus } from 'lucide-react';
import { dbClient } from './supabaseClient';

interface FreedomData {
  monthlyExpenses: number;
  freedomExpenses: number;
  emergencyMonths: number;
  totalDebt: number;
  monthlySavingsGoal: number;
  targetDate: string;
}

interface IncomeStream {
  id: number;
  name: string;
  monthlyRevenue: number;
  hoursPerWeek: number;
  growthPotential: string;
  stressLevel: string;
  systemScore: number;
  category: string;
  target90Day: number;
  target12Month: number;
  keyMetric: string;
  status: string;
}

interface WeeklyReview {
  date: string;
  wins: string;
  blocks: string;
  nextWeekFocus: string;
}

interface PriorityEntry {
  id: string;
  period: string; // e.g., "Dec 1-7, 2025"
  priorities: string[];
  completed: boolean[];
  createdAt: string;
}

interface ReviewHistory {
  date: string;
  wins: string;
  blocks: string;
  nextWeekFocus: string;
  createdAt: string;
}

interface Expense {
  id: number;
  category: string;
  item: string;
  amount: number;
  type: 'fixed' | 'variable';
}

interface ExcelIncomeStream {
  id: number;
  name: string;
  projected: number;
  actual: number;
  category: string;
}

interface MonthlyData {
  month: string;
  emergencyFund: number;
  debtPayments: number;
  savingsGoal: number;
}

export default function FinancialFreedomTracker() {
  const [activeTab, setActiveTab] = useState('freedom-number');
  const [loading, setLoading] = useState(true);
  
  const [freedomData, setFreedomData] = useState<FreedomData>({
    monthlyExpenses: 0,
    freedomExpenses: 0,
    emergencyMonths: 6,
    totalDebt: 0,
    monthlySavingsGoal: 0,
    targetDate: ''
  });
  
  const [incomeStreams, setIncomeStreams] = useState<IncomeStream[]>([]);
  const [priorities, setPriorities] = useState<string[]>(['', '', '']);
  const [priorityCompletion, setPriorityCompletion] = useState<boolean[]>([false, false, false]);
  const [priorityHistory, setPriorityHistory] = useState<PriorityEntry[]>([]);
  const [weeklyReview, setWeeklyReview] = useState<WeeklyReview>({
    date: new Date().toISOString().split('T')[0],
    wins: '',
    blocks: '',
    nextWeekFocus: ''
  });
  const [reviewHistory, setReviewHistory] = useState<ReviewHistory[]>([]);
  const [showPriorityHistory, setShowPriorityHistory] = useState(false);
  const [showReviewHistory, setShowReviewHistory] = useState(false);

  // Excel Generator State
  const [expenses, setExpenses] = useState<Expense[]>([
    { id: 1, category: 'Housing', item: 'Rent/Mortgage', amount: 0, type: 'fixed' },
    { id: 2, category: 'Housing', item: 'Utilities', amount: 0, type: 'variable' },
    { id: 3, category: 'Food', item: 'Groceries', amount: 0, type: 'variable' },
    { id: 4, category: 'Food', item: 'Dining Out', amount: 0, type: 'variable' },
    { id: 5, category: 'Transportation', item: 'Car Payment', amount: 0, type: 'fixed' },
    { id: 6, category: 'Transportation', item: 'Gas', amount: 0, type: 'variable' },
  ]);

  const [excelIncomeStreams, setExcelIncomeStreams] = useState<ExcelIncomeStream[]>([
    { id: 1, name: 'Primary Business', projected: 0, actual: 0, category: 'business' },
    { id: 2, name: 'Side Hustle', projected: 0, actual: 0, category: 'business' },
  ]);

  const [monthlyData, setMonthlyData] = useState<MonthlyData>({
    month: new Date().toISOString().slice(0, 7),
    emergencyFund: 0,
    debtPayments: 0,
    savingsGoal: 0
  });

  useEffect(() => {
    const initializeApp = async () => {
      const currentMonth = new Date().toISOString().slice(0, 7);
      try {
        const freedomDataStr = localStorage.getItem('finance:freedom-data');
        if (freedomDataStr) setFreedomData(JSON.parse(freedomDataStr));
        
        const incomeStreamsStr = localStorage.getItem('finance:income-streams');
        if (incomeStreamsStr) setIncomeStreams(JSON.parse(incomeStreamsStr));
        
        const prioritiesStr = localStorage.getItem('finance:priorities');
        if (prioritiesStr) setPriorities(JSON.parse(prioritiesStr));
        
        const priorityCompletionStr = localStorage.getItem('finance:priority-completion');
        if (priorityCompletionStr) setPriorityCompletion(JSON.parse(priorityCompletionStr));
        
        const priorityHistoryStr = localStorage.getItem('finance:priority-history');
        if (priorityHistoryStr) setPriorityHistory(JSON.parse(priorityHistoryStr));
        
        const weeklyReviewStr = localStorage.getItem('finance:weekly-review');
        if (weeklyReviewStr) setWeeklyReview(JSON.parse(weeklyReviewStr));
        
        const reviewHistoryStr = localStorage.getItem('finance:review-history');
        if (reviewHistoryStr) setReviewHistory(JSON.parse(reviewHistoryStr));

        // Load current month's data
        await loadMonthData(currentMonth);
      } catch (error) {
        console.log('Starting fresh - no existing data');
      }
      setLoading(false);
    };

    initializeApp();
  }, []);

  const loadMonthData = async (month: string) => {
    try {
      const expensesData = await dbClient.getExpenses(month);
      const monthlyData = await dbClient.getIncomes(month);
      
      // Load expenses or reset to defaults
      if (expensesData && expensesData.length > 0) {
        const formattedExpenses: Expense[] = expensesData.map((exp: any) => ({
          id: parseInt(exp.id.toString()),
          category: exp.category,
          item: exp.item,
          amount: exp.amount,
          type: exp.type as 'fixed' | 'variable'
        }));
        setExpenses(formattedExpenses);
      } else {
        setExpenses([
          { id: 1, category: 'Housing', item: 'Rent/Mortgage', amount: 0, type: 'fixed' },
          { id: 2, category: 'Housing', item: 'Utilities', amount: 0, type: 'variable' },
          { id: 3, category: 'Food', item: 'Groceries', amount: 0, type: 'variable' },
          { id: 4, category: 'Food', item: 'Dining Out', amount: 0, type: 'variable' },
          { id: 5, category: 'Transportation', item: 'Car Payment', amount: 0, type: 'fixed' },
          { id: 6, category: 'Transportation', item: 'Gas', amount: 0, type: 'variable' },
        ]);
      }
      
      // Load incomes or reset to defaults
      if (monthlyData && monthlyData.incomes) {
        setExcelIncomeStreams(monthlyData.incomes);
      } else {
        setExcelIncomeStreams([
          { id: 1, name: 'Primary Business', projected: 0, actual: 0, category: 'business' },
          { id: 2, name: 'Side Hustle', projected: 0, actual: 0, category: 'business' },
        ]);
      }
      
      // Load monthly data or reset to defaults
      if (monthlyData) {
        setMonthlyData({
          month,
          emergencyFund: monthlyData.emergency_fund || 0,
          debtPayments: monthlyData.debt_payments || 0,
          savingsGoal: monthlyData.savings_goal || 0
        });
      } else {
        setMonthlyData({
          month,
          emergencyFund: 0,
          debtPayments: 0,
          savingsGoal: 0
        });
      }
    } catch (error) {
      console.log('Starting fresh for month:', month, error);
      setExpenses([
        { id: 1, category: 'Housing', item: 'Rent/Mortgage', amount: 0, type: 'fixed' },
        { id: 2, category: 'Housing', item: 'Utilities', amount: 0, type: 'variable' },
        { id: 3, category: 'Food', item: 'Groceries', amount: 0, type: 'variable' },
        { id: 4, category: 'Food', item: 'Dining Out', amount: 0, type: 'variable' },
        { id: 5, category: 'Transportation', item: 'Car Payment', amount: 0, type: 'fixed' },
        { id: 6, category: 'Transportation', item: 'Gas', amount: 0, type: 'variable' },
      ]);
      setExcelIncomeStreams([
        { id: 1, name: 'Primary Business', projected: 0, actual: 0, category: 'business' },
        { id: 2, name: 'Side Hustle', projected: 0, actual: 0, category: 'business' },
      ]);
      setMonthlyData({
        month,
        emergencyFund: 0,
        debtPayments: 0,
        savingsGoal: 0
      });
    }
  };

  const changeMonth = async (newMonth: string) => {
    // Save current month first
    await saveMonthData(monthlyData.month);
    // Load new month
    await loadMonthData(newMonth);
  };

  const saveMonthData = async (month: string) => {
    try {
      await dbClient.saveExpenses(month, expenses);
      await dbClient.saveIncomes(month, excelIncomeStreams);
      await dbClient.updateMonthlyData(month, {
        emergencyFund: monthlyData.emergencyFund,
        debtPayments: monthlyData.debtPayments,
        savingsGoal: monthlyData.savingsGoal
      });
    } catch (error) {
      console.error('Error saving month data:', error);
    }
  };

  const saveData = (key: string, data: any) => {
    try {
      localStorage.setItem(`finance:${key}`, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const updateFreedomData = (field: keyof FreedomData, value: any) => {
    const updated = { ...freedomData, [field]: value };
    setFreedomData(updated);
    saveData('freedom-data', updated);
  };

  const calculateFreedomNumber = () => {
    const emergencyFund = freedomData.monthlyExpenses * freedomData.emergencyMonths;
    const monthlyFreedom = freedomData.freedomExpenses + freedomData.monthlySavingsGoal;
    return { emergencyFund, monthlyFreedom, totalDebt: freedomData.totalDebt };
  };

  const addIncomeStream = () => {
    const newStream = {
      id: Date.now(),
      name: '',
      monthlyRevenue: 0,
      hoursPerWeek: 0,
      growthPotential: 'medium',
      stressLevel: 'medium',
      systemScore: 5,
      category: 'keep-grow',
      target90Day: 0,
      target12Month: 0,
      keyMetric: '',
      status: 'green'
    };
    const updated = [...incomeStreams, newStream];
    setIncomeStreams(updated);
    saveData('income-streams', updated);
  };

  const updateIncomeStream = (id: number, field: keyof IncomeStream, value: any) => {
    const updated = incomeStreams.map(stream => 
      stream.id === id ? { ...stream, [field]: value } : stream
    );
    setIncomeStreams(updated);
    saveData('income-streams', updated);
  };

  const deleteIncomeStream = (id: number) => {
    const updated = incomeStreams.filter(stream => stream.id !== id);
    setIncomeStreams(updated);
    saveData('income-streams', updated);
  };

  const updatePriority = (index: number, value: string) => {
    const updated = [...priorities];
    updated[index] = value;
    setPriorities(updated);
    saveData('priorities', updated);
  };

  const updatePriorityCompletion = (index: number, completed: boolean) => {
    const updated = [...priorityCompletion];
    updated[index] = completed;
    setPriorityCompletion(updated);
    saveData('priority-completion', updated);
  };

  const archivePriorities = () => {
    if (priorities.some(p => p.trim() !== '')) {
      const entry: PriorityEntry = {
        id: Date.now().toString(),
        period: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        priorities,
        completed: priorityCompletion,
        createdAt: new Date().toISOString()
      };
      const updated = [entry, ...priorityHistory];
      setPriorityHistory(updated);
      saveData('priority-history', updated);
      // Reset for new week
      setPriorities(['', '', '']);
      setPriorityCompletion([false, false, false]);
      saveData('priorities', ['', '', '']);
      saveData('priority-completion', [false, false, false]);
    }
  };

  const updateWeeklyReview = (field: keyof WeeklyReview, value: string) => {
    const updated = { ...weeklyReview, [field]: value };
    setWeeklyReview(updated);
    saveData('weekly-review', updated);
  };

  const archiveReview = () => {
    if (weeklyReview.wins.trim() || weeklyReview.blocks.trim() || weeklyReview.nextWeekFocus.trim()) {
      const entry: ReviewHistory = {
        date: weeklyReview.date,
        wins: weeklyReview.wins,
        blocks: weeklyReview.blocks,
        nextWeekFocus: weeklyReview.nextWeekFocus,
        createdAt: new Date().toISOString()
      };
      const updated = [entry, ...reviewHistory];
      setReviewHistory(updated);
      saveData('review-history', updated);
      // Reset for next week
      setWeeklyReview({
        date: new Date().toISOString().split('T')[0],
        wins: '',
        blocks: '',
        nextWeekFocus: ''
      });
      saveData('weekly-review', {
        date: new Date().toISOString().split('T')[0],
        wins: '',
        blocks: '',
        nextWeekFocus: ''
      });
    }
  };

  // Excel Generator Functions
  const addExpense = () => {
    const updated: Expense[] = [...expenses, {
      id: Date.now(),
      category: 'Other',
      item: '',
      amount: 0,
      type: 'variable'
    }];
    setExpenses(updated);
    saveMonthData(monthlyData.month);
  };

  const updateExpense = (id: number, field: keyof Expense, value: any) => {
    const updated: Expense[] = expenses.map(exp => 
      exp.id === id ? { ...exp, [field]: value } : exp
    );
    setExpenses(updated);
    saveMonthData(monthlyData.month);
  };

  const deleteExpense = (id: number) => {
    const updated: Expense[] = expenses.filter(exp => exp.id !== id);
    setExpenses(updated);
    saveMonthData(monthlyData.month);
  };

  const addExcelIncome = () => {
    const updated: ExcelIncomeStream[] = [...excelIncomeStreams, {
      id: Date.now(),
      name: '',
      projected: 0,
      actual: 0,
      category: 'business'
    }];
    setExcelIncomeStreams(updated);
    saveMonthData(monthlyData.month);
  };

  const updateExcelIncome = (id: number, field: keyof ExcelIncomeStream, value: any) => {
    const updated: ExcelIncomeStream[] = excelIncomeStreams.map(inc => 
      inc.id === id ? { ...inc, [field]: value } : inc
    );
    setExcelIncomeStreams(updated);
    saveMonthData(monthlyData.month);
  };

  const deleteExcelIncome = (id: number) => {
    const updated: ExcelIncomeStream[] = excelIncomeStreams.filter(inc => inc.id !== id);
    setExcelIncomeStreams(updated);
    saveMonthData(monthlyData.month);
  };

  const generateCSV = () => {
    let csv = 'Financial Freedom Tracker - ' + monthlyData.month + '\n\n';
    
    const totalExpenses = expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);
    const totalProjectedIncome = excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0);
    const totalActualIncome = excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.actual || 0), 0);
    
    csv += 'INCOME STREAMS\n';
    csv += 'Stream Name,Projected Monthly,Actual Monthly,Variance,Category\n';
    excelIncomeStreams.forEach(inc => {
      const variance = Number(inc.actual || 0) - Number(inc.projected || 0);
      csv += `"${inc.name}",₵${inc.projected},₵${inc.actual},₵${variance},${inc.category}\n`;
    });
    csv += `TOTAL INCOME,₵${totalProjectedIncome},₵${totalActualIncome},₵${totalActualIncome - totalProjectedIncome}\n\n`;

    csv += 'EXPENSES\n';
    csv += 'Category,Item,Amount,Type\n';
    expenses.forEach(exp => {
      csv += `"${exp.category}","${exp.item}",₵${exp.amount},${exp.type}\n`;
    });
    csv += `TOTAL EXPENSES,,,₵${totalExpenses}\n\n`;

    csv += 'SAVINGS & DEBT\n';
    csv += 'Item,Amount\n';
    csv += `Emergency Fund Balance,₵${monthlyData.emergencyFund}\n`;
    csv += `Monthly Debt Payments,₵${monthlyData.debtPayments}\n`;
    csv += `Monthly Savings Goal,₵${monthlyData.savingsGoal}\n\n`;

    csv += 'SUMMARY\n';
    csv += 'Item,Projected,Actual\n';
    csv += `Total Income,₵${totalProjectedIncome},₵${totalActualIncome}\n`;
    csv += `Total Expenses,₵${totalExpenses},₵${totalExpenses}\n`;
    csv += `Debt Payments,₵${monthlyData.debtPayments},₵${monthlyData.debtPayments}\n`;
    csv += `Savings Goal,₵${monthlyData.savingsGoal},₵${monthlyData.savingsGoal}\n`;
    
    const projectedSurplus = totalProjectedIncome - totalExpenses - monthlyData.debtPayments - monthlyData.savingsGoal;
    const actualSurplus = totalActualIncome - totalExpenses - monthlyData.debtPayments - monthlyData.savingsGoal;
    csv += `NET SURPLUS/DEFICIT,₵${projectedSurplus.toFixed(2)},₵${actualSurplus.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Financial-Tracker-${monthlyData.month}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalCurrentRevenue = incomeStreams.reduce((sum, s) => sum + Number(s.monthlyRevenue || 0), 0);
  const { emergencyFund, monthlyFreedom, totalDebt } = calculateFreedomNumber();
  const progressPercent = monthlyFreedom > 0 ? Math.min((totalCurrentRevenue / monthlyFreedom) * 100, 100) : 0;

  const getStatusColor = (status: string) => {
    const colors = {
      green: 'bg-green-100 text-green-800 border-green-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      red: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status as keyof typeof colors] || colors.green;
  };

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl font-semibold text-gray-700">Loading your financial data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Financial Freedom Execution System</h1>
        <p className="text-gray-600">Structure your path from prayer to action</p>
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Local Version:</strong> Your data is saved locally in your browser's storage.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Current Monthly Income</p>
              <p className="text-2xl font-bold text-gray-800">₵{totalCurrentRevenue.toLocaleString()}</p>
            </div>
            <DollarSign className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Freedom Target</p>
              <p className="text-2xl font-bold text-gray-800">₵{monthlyFreedom.toLocaleString()}/mo</p>
            </div>
            <Target className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Progress to Freedom</p>
              <p className="text-2xl font-bold text-gray-800">{progressPercent.toFixed(1)}%</p>
            </div>
            <TrendingUp className="text-purple-500" size={32} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="mb-2 flex justify-between text-sm text-gray-600">
          <span>Journey to Financial Freedom</span>
          <span>{progressPercent.toFixed(1)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div 
            className="bg-gradient-to-r from-blue-500 to-green-500 h-4 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap">
            {[
              { id: 'freedom-number', label: 'Freedom Number' },
              { id: 'income-streams', label: 'Income Streams' },
              { id: 'priorities', label: 'Top 3 Priorities' },
              { id: 'weekly-review', label: 'Weekly Review' },
              { id: 'excel-tracker', label: 'Expense Tracker' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'freedom-number' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Define Your Freedom Number</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Monthly Expenses
                    </label>
                    <input
                      type="number"
                      value={freedomData.monthlyExpenses}
                      onChange={(e) => updateFreedomData('monthlyExpenses', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="5000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Freedom Lifestyle Expenses
                    </label>
                    <input
                      type="number"
                      value={freedomData.freedomExpenses}
                      onChange={(e) => updateFreedomData('freedomExpenses', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="7000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Fund (Months)
                    </label>
                    <input
                      type="number"
                      value={freedomData.emergencyMonths}
                      onChange={(e) => updateFreedomData('emergencyMonths', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="6"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Debt to Eliminate
                    </label>
                    <input
                      type="number"
                      value={freedomData.totalDebt}
                      onChange={(e) => updateFreedomData('totalDebt', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="25000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Savings Goal
                    </label>
                    <input
                      type="number"
                      value={freedomData.monthlySavingsGoal}
                      onChange={(e) => updateFreedomData('monthlySavingsGoal', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Freedom Date
                    </label>
                    <input
                      type="date"
                      value={freedomData.targetDate}
                      onChange={(e) => updateFreedomData('targetDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3">Your Freedom Numbers:</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Emergency Fund Target:</span>
                    <span className="font-bold text-gray-900">₵{emergencyFund.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Monthly Freedom Income Needed:</span>
                    <span className="font-bold text-gray-900">₵{monthlyFreedom.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Total Debt to Clear:</span>
                    <span className="font-bold text-gray-900">₵{totalDebt.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-300">
                    <span className="text-gray-700">Gap to Close:</span>
                    <span className="font-bold text-blue-900">
                      ₵{Math.max(0, monthlyFreedom - totalCurrentRevenue).toLocaleString()}/month
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'income-streams' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Income Stream Audit</h3>
                <button
                  onClick={addIncomeStream}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  <PlusCircle size={20} />
                  Add Stream
                </button>
              </div>

              {incomeStreams.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No income streams yet. Click "Add Stream" to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {incomeStreams.map(stream => (
                    <div key={stream.id} className={`border-2 rounded-lg p-4 ${getStatusColor(stream.status)}`}>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium mb-1">Stream Name</label>
                          <input
                            type="text"
                            value={stream.name}
                            onChange={(e) => updateIncomeStream(stream.id, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                            placeholder="E.g., Consulting, E-commerce"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">Monthly Revenue</label>
                          <input
                            type="number"
                            value={stream.monthlyRevenue}
                            onChange={(e) => updateIncomeStream(stream.id, 'monthlyRevenue', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                            placeholder="5000"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">Hours/Week</label>
                          <input
                            type="number"
                            value={stream.hoursPerWeek}
                            onChange={(e) => updateIncomeStream(stream.id, 'hoursPerWeek', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                            placeholder="20"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Growth</label>
                          <select
                            value={stream.growthPotential}
                            onChange={(e) => updateIncomeStream(stream.id, 'growthPotential', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">Stress</label>
                          <select
                            value={stream.stressLevel}
                            onChange={(e) => updateIncomeStream(stream.id, 'stressLevel', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">System (1-10)</label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={stream.systemScore}
                            onChange={(e) => updateIncomeStream(stream.id, 'systemScore', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">Category</label>
                          <select
                            value={stream.category}
                            onChange={(e) => updateIncomeStream(stream.id, 'category', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                          >
                            <option value="keep-grow">Keep & Grow</option>
                            <option value="keep-systematize">Keep & Systematize</option>
                            <option value="keep-maintain">Keep & Maintain</option>
                            <option value="fix-or-kill">Fix or Kill</option>
                            <option value="kill-now">Kill Now</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">Status</label>
                          <select
                            value={stream.status}
                            onChange={(e) => updateIncomeStream(stream.id, 'status', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                          >
                            <option value="green">Green</option>
                            <option value="yellow">Yellow</option>
                            <option value="red">Red</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">90-Day Target</label>
                          <input
                            type="number"
                            value={stream.target90Day}
                            onChange={(e) => updateIncomeStream(stream.id, 'target90Day', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                            placeholder="6000"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">12-Month Target</label>
                          <input
                            type="number"
                            value={stream.target12Month}
                            onChange={(e) => updateIncomeStream(stream.id, 'target12Month', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                            placeholder="10000"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium mb-1">Key Metric</label>
                          <input
                            type="text"
                            value={stream.keyMetric}
                            onChange={(e) => updateIncomeStream(stream.id, 'keyMetric', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                            placeholder="New clients, Sales"
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center text-sm">
                        <div className="text-xs text-gray-600">
                          Revenue/hour: ₵{stream.hoursPerWeek > 0 ? ((stream.monthlyRevenue / 4) / stream.hoursPerWeek).toFixed(2) : '0'}
                        </div>
                        <button
                          onClick={() => deleteIncomeStream(stream.id)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'priorities' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Top 3 Priorities This Week</h3>
                  <p className="text-sm text-gray-600">What are the 3 most important things to move the needle?</p>
                </div>
                <div className="space-x-2">
                  <button
                    onClick={() => setShowPriorityHistory(!showPriorityHistory)}
                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                  >
                    {showPriorityHistory ? 'Hide' : 'Show'} History
                  </button>
                  <button
                    onClick={archivePriorities}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors"
                  >
                    Archive & Reset
                  </button>
                </div>
              </div>
              
              {priorities.map((priority, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={priority}
                      onChange={(e) => updatePriority(index, e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                      placeholder={`Priority ${index + 1}: What will make the biggest impact?`}
                    />
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={priorityCompletion[index] || false}
                        onChange={(e) => updatePriorityCompletion(index, e.target.checked)}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm text-gray-600">Mark as completed</span>
                    </label>
                  </div>
                </div>
              ))}

              {showPriorityHistory && priorityHistory.length > 0 && (
                <div className="border-t pt-6 mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">Priority History</h4>
                  <div className="space-y-4">
                    {priorityHistory.map((entry) => (
                      <div key={entry.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-3">
                          <h5 className="font-semibold text-gray-800">{entry.period}</h5>
                          <span className="text-xs text-gray-500">
                            {new Date(entry.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {entry.priorities.map((priority, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                checked={entry.completed[idx] || false}
                                disabled
                                className="w-4 h-4 mt-1 rounded"
                              />
                              <span className={`text-sm ${entry.completed[idx] ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                                {priority}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-yellow-900 mb-1">Remember:</h4>
                    <p className="text-sm text-yellow-800">Focus beats hustle. Work on one priority at a time.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'weekly-review' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Weekly Review</h3>
                <div className="space-x-2">
                  <button
                    onClick={() => setShowReviewHistory(!showReviewHistory)}
                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
                  >
                    {showReviewHistory ? 'Hide' : 'Show'} History
                  </button>
                  <button
                    onClick={archiveReview}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors"
                  >
                    Archive & Reset
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">Reflect on this week and plan for next. Do this every Friday.</p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Review Date</label>
                <input
                  type="date"
                  value={weeklyReview.date}
                  onChange={(e) => updateWeeklyReview('date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Wins This Week (Celebrate!)</label>
                <textarea
                  value={weeklyReview.wins}
                  onChange={(e) => updateWeeklyReview('wins', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  placeholder="What went well? What progress did you make?"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">What's Blocking Progress?</label>
                <textarea
                  value={weeklyReview.blocks}
                  onChange={(e) => updateWeeklyReview('blocks', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  placeholder="What obstacles came up? What's slowing you down?"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Top Focus for Next Week</label>
                <textarea
                  value={weeklyReview.nextWeekFocus}
                  onChange={(e) => updateWeeklyReview('nextWeekFocus', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  placeholder="What's the ONE thing that will make next week successful?"
                />
              </div>

              {showReviewHistory && reviewHistory.length > 0 && (
                <div className="border-t pt-6 mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">Review History</h4>
                  <div className="space-y-4">
                    {reviewHistory.map((review, idx) => (
                      <details key={idx} className="border rounded-lg p-4 bg-gray-50">
                        <summary className="cursor-pointer font-semibold text-gray-800">
                          {new Date(review.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </summary>
                        <div className="mt-4 space-y-3">
                          <div>
                            <h5 className="font-semibold text-green-700 mb-1">Wins:</h5>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{review.wins}</p>
                          </div>
                          <div>
                            <h5 className="font-semibold text-orange-700 mb-1">Blocks:</h5>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{review.blocks}</p>
                          </div>
                          <div>
                            <h5 className="font-semibold text-blue-700 mb-1">Next Week Focus:</h5>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{review.nextWeekFocus}</p>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Weekly Rhythm:</h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>• Monday: Review dashboard, set top 3 priorities</li>
                      <li>• Daily: 1-2 hours on highest-leverage activity</li>
                      <li>• Friday: Update numbers, celebrate wins</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'excel-tracker' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Expense Tracker & Export</h3>
                  <p className="text-sm text-gray-600">Track income, expenses, and export to Excel</p>
                </div>
                <button
                  onClick={generateCSV}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  <Download size={20} />
                  Export to Excel
                </button>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tracking Month</label>
                <input
                  type="month"
                  value={monthlyData.month}
                  onChange={(e) => changeMonth(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
                  <p className="text-sm text-gray-600">Total Projected Income</p>
                  <p className="text-2xl font-bold text-gray-800">₵{excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0).toLocaleString()}</p>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
                  <p className="text-sm text-gray-600">Total Expenses</p>
                  <p className="text-2xl font-bold text-gray-800">₵{expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0).toLocaleString()}</p>
                </div>
                
                <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${(excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0) - expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0) - monthlyData.debtPayments - monthlyData.savingsGoal) >= 0 ? 'border-blue-500' : 'border-orange-500'}`}>
                  <p className="text-sm text-gray-600">Projected Surplus/Deficit</p>
                  <p className={`text-2xl font-bold ${(excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0) - expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0) - monthlyData.debtPayments - monthlyData.savingsGoal) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₵{(excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0) - expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0) - monthlyData.debtPayments - monthlyData.savingsGoal).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Income Streams</h2>
                    <button
                      onClick={addExcelIncome}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                    >
                      <Plus size={16} />
                      Add Income
                    </button>
                  </div>

                  <div className="space-y-3">
                    {excelIncomeStreams.map(income => (
                      <div key={income.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="grid grid-cols-1 gap-3">
                          <input
                            type="text"
                            placeholder="Income stream name"
                            value={income.name}
                            onChange={(e) => updateExcelIncome(income.id, 'name', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Projected</label>
                              <input
                                type="number"
                                placeholder="0"
                                value={income.projected}
                                onChange={(e) => updateExcelIncome(income.id, 'projected', Number(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Actual</label>
                              <input
                                type="number"
                                placeholder="0"
                                value={income.actual}
                                onChange={(e) => updateExcelIncome(income.id, 'actual', Number(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Variance</label>
                              <div className={`px-3 py-2 rounded text-sm font-semibold ${
                                (income.actual - income.projected) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                ₵{(income.actual - income.projected).toLocaleString()}
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-between items-center">
                            <select
                              value={income.category}
                              onChange={(e) => updateExcelIncome(income.id, 'category', e.target.value)}
                              className="px-3 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="business">Business</option>
                              <option value="investment">Investment</option>
                              <option value="passive">Passive</option>
                              <option value="other">Other</option>
                            </select>
                            
                            <button
                              onClick={() => deleteExcelIncome(income.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Monthly Expenses</h2>
                    <button
                      onClick={addExpense}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                    >
                      <Plus size={16} />
                      Add Expense
                    </button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {expenses.map(expense => (
                      <div key={expense.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="Category"
                            value={expense.category}
                            onChange={(e) => updateExpense(expense.id, 'category', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          
                          <input
                            type="text"
                            placeholder="Item"
                            value={expense.item}
                            onChange={(e) => updateExpense(expense.id, 'item', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            placeholder="Amount"
                            value={expense.amount}
                            onChange={(e) => updateExpense(expense.id, 'amount', Number(e.target.value))}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          
                          <div className="flex gap-2 items-center">
                            <select
                              value={expense.type}
                              onChange={(e) => updateExpense(expense.id, 'type', e.target.value as 'fixed' | 'variable')}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="fixed">Fixed</option>
                              <option value="variable">Variable</option>
                            </select>
                            
                            <button
                              onClick={() => deleteExpense(expense.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Expenses by Category</h3>
                  <div className="space-y-3">
                    {Object.entries(expenses.reduce((acc, exp) => {
                      acc[exp.category] = (acc[exp.category] || 0) + Number(exp.amount || 0);
                      return acc;
                    }, {} as Record<string, number>)).map(([category, amount]) => (
                      <div key={category} className="flex justify-between items-center pb-2 border-b border-gray-200">
                        <span className="text-gray-700 font-medium">{category}</span>
                        <span className="text-gray-900 font-bold">₵{amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Savings & Debt</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Fund Balance</label>
                      <input
                        type="number"
                        value={monthlyData.emergencyFund}
                        onChange={(e) => {
                          const updated = { ...monthlyData, emergencyFund: Number(e.target.value) };
                          setMonthlyData(updated);
                          saveMonthData(updated.month);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Debt Payments</label>
                      <input
                        type="number"
                        value={monthlyData.debtPayments}
                        onChange={(e) => {
                          const updated = { ...monthlyData, debtPayments: Number(e.target.value) };
                          setMonthlyData(updated);
                          saveMonthData(updated.month);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Savings Goal</label>
                      <input
                        type="number"
                        value={monthlyData.savingsGoal}
                        onChange={(e) => {
                          const updated = { ...monthlyData, savingsGoal: Number(e.target.value) };
                          setMonthlyData(updated);
                          saveMonthData(updated.month);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Calculator size={32} />
                  <h3 className="text-xl font-bold">Monthly Summary</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="opacity-90 mb-1">Total Income</p>
                    <p className="text-xl font-bold">₵{excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="opacity-90 mb-1">Total Expenses</p>
                    <p className="text-xl font-bold">₵{expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="opacity-90 mb-1">Debt + Savings</p>
                    <p className="text-xl font-bold">₵{(monthlyData.debtPayments + monthlyData.savingsGoal).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="opacity-90 mb-1">Net Position</p>
                    <p className={`text-xl font-bold ${(excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0) - expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0) - monthlyData.debtPayments - monthlyData.savingsGoal) >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                      ₵{(excelIncomeStreams.reduce((sum, inc) => sum + Number(inc.projected || 0), 0) - expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0) - monthlyData.debtPayments - monthlyData.savingsGoal).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-white border-opacity-30">
                  <p className="text-sm opacity-90">Click "Export to Excel" to download a CSV file that opens in Excel, Google Sheets, or any spreadsheet program.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <h3 className="text-xl font-bold mb-4">Next Steps:</h3>
        <ol className="space-y-2 text-sm">
          <li>1. Fill out your Freedom Number</li>
          <li>2. Add all income streams</li>
          <li>3. Categorize each stream honestly</li>
          <li>4. Set Top 3 Priorities for this month</li>
          <li>5. Schedule weekly review (Friday afternoons)</li>
          <li>6. Review together with your wife</li>
        </ol>
      </div>
    </div>
  );
}
